import { useEffect, useState, useCallback } from "react";
import { onSnapshot } from "firebase/firestore";
import { auth } from "@/api/firebase/firebase.config";
import { collectionRef } from "@/api/firebase/collectionOperations";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { User } from "firebase/auth";

export const useAuth = () => {
  const [isLoading, setIsLoading] = useState(true);
  const profile = useCurrentUserProfile((s) => s.user);
  const setUserProfile = useCurrentUserProfile((s) => s.setUser);

  // Function to update user data in store
  const updateUserData = useCallback(
    async (user: User) => {
      try {
        const idTokenResult = await user.getIdTokenResult();
        const isAdmin = !!idTokenResult.claims.admin;

        const profileDocumentRef = collectionRef.Profiles.doc(user.uid).ref();
        return onSnapshot(profileDocumentRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            setUserProfile({
              ...data,
              emailVerified: user.emailVerified,
              isAdmin,
            });
          } else {
            console.log("User Profile does not exist!");
          }
          setIsLoading(false);
        });
      } catch (error) {
        console.error("Error updating user data:", error);
        setIsLoading(false);
        return () => {};
      }
    },
    [setUserProfile]
  );

  useEffect(() => {
    setIsLoading(true);
    let unsubscribeSnapshot = () => {};

    // Listen for auth state changes (sign in/out)
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        setUserProfile(null);
        setIsLoading(false);
      }
    });

    // Listen for ID token changes (claims, email verification, etc.)
    const unsubscribeIdToken = auth.onIdTokenChanged(async (user) => {
      if (user) {
        // Clean up previous listener if exists
        unsubscribeSnapshot();
        // Set up new listener with updated user data
        unsubscribeSnapshot = await updateUserData(user);
      } else {
        setIsLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeIdToken();
      unsubscribeSnapshot();
    };
  }, [updateUserData]);

  // Helper to manually refresh user data
  const refreshUserData = async () => {
    if (auth.currentUser) {
      try {
        await auth.currentUser.reload();
        await updateUserData(auth.currentUser);
        return true;
      } catch (error) {
        console.error("Error refreshing user data:", error);
        return false;
      }
    }
    return false;
  };

  return { isLoading, profile, refreshUserData };
};
